import React, { useContext, useState } from 'react';
import dayjs from '~/utils/dayjsConfig';
import { UserContext } from '~/contexts/UserContext';
import { useFulfillmentLifecycleData } from './hooks/useFulfillmentLifecycleData';
import { Card, CardContent, CardHeader, CardTitle } from '~/shadcn/components/ui/Card';
import { Badge } from '~/shadcn/components/ui/Badge';
import { Skeleton } from '~/shadcn/components/ui/Skeleton';
import { Alert, AlertDescription, AlertTitle } from '~/shadcn/components/ui/Alert';

/**
 * Risk level color mappings
 */
const RISK_BADGE_VARIANTS = {
    CRITICAL: 'destructive',
    AT_RISK: 'warning',
    ON_TRACK: 'success',
};

const RISK_LABELS = {
    CRITICAL: 'Critical',
    AT_RISK: 'At Risk',
    ON_TRACK: 'On Track',
};

/**
 * FulfillmentLifecycleDashboard
 *
 * Main container component for the Fulfillment Lifecycle dashboard.
 * Displays route status, risk indicators, and throughput analytics.
 *
 * State:
 * - selectedDate: Operating date for the dashboard (default: today)
 * - filters: Active filter selections (deliveryTypes, orderSources, etc.)
 *
 * Data fetching handled by useFulfillmentLifecycleData hook.
 */
const FulfillmentLifecycleDashboard = () => {
    const { getFacilityID, getFacilityName } = useContext(UserContext);
    const facilityId = getFacilityID();
    const facilityName = getFacilityName();

    // State for date selection and filters
    const [selectedDate, setSelectedDate] = useState(dayjs().format('YYYY-MM-DD'));
    const [filters, setFilters] = useState({
        orderSources: [],
        deliveryTypes: [],
        statusFilter: 'all', // 'all' | 'critical' | 'atRisk' | 'onTrack'
        milestone: 'all', // 'all' | 'labels' | 'packing' | 'loading' | 'complete'
    });

    // Fetch dashboard data
    const { routes, summary, loading, error, refetch } = useFulfillmentLifecycleData(facilityId, selectedDate, {
        orderSources: filters.orderSources,
        deliveryTypes: filters.deliveryTypes,
    });

    // Render loading skeleton
    if (loading && routes.length === 0) {
        return (
            <div className="fulfillment-lifecycle-dashboard p-6 space-y-6">
                <DashboardHeaderSkeleton />
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {[1, 2, 3, 4, 5, 6].map((i) => (
                        <RouteCardSkeleton key={i} />
                    ))}
                </div>
            </div>
        );
    }

    // Render error state
    if (error) {
        return (
            <div className="fulfillment-lifecycle-dashboard p-6">
                <Alert variant="destructive">
                    <AlertTitle>Error loading dashboard</AlertTitle>
                    <AlertDescription>{error}</AlertDescription>
                </Alert>
            </div>
        );
    }

    // Render empty state
    if (!loading && routes.length === 0) {
        return (
            <div className="fulfillment-lifecycle-dashboard p-6 space-y-6">
                <DashboardHeader
                    facilityName={facilityName}
                    selectedDate={selectedDate}
                    onDateChange={setSelectedDate}
                />
                <Alert>
                    <AlertTitle>No routes found</AlertTitle>
                    <AlertDescription>
                        No routes found for {facilityName} on {dayjs(selectedDate).format('MMMM D, YYYY')}.
                    </AlertDescription>
                </Alert>
            </div>
        );
    }

    return (
        <div className="fulfillment-lifecycle-dashboard p-6 space-y-6">
            {/* Header */}
            <DashboardHeader facilityName={facilityName} selectedDate={selectedDate} onDateChange={setSelectedDate} />

            {/* Summary Alerts */}
            <SummaryAlerts summary={summary} />

            {/* Routes Grid - Placeholder for Ticket 3e */}
            <div className="space-y-4">
                <h2 className="text-lg font-semibold">Routes ({routes.length})</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {routes.map((route) => (
                        <RouteCardPreview key={route.lineHaulId} route={route} />
                    ))}
                </div>
            </div>
        </div>
    );
};

/**
 * Dashboard header with title and date info
 * TODO: Replace with full DashboardHeader component in Ticket 3c
 */
const DashboardHeader = ({ facilityName, selectedDate, onDateChange }) => {
    return (
        <div className="space-y-2">
            <h1 className="text-2xl font-bold">Shipment Status Dashboard</h1>
            <p className="text-muted-foreground">
                {facilityName} &bull; {dayjs(selectedDate).format('MMMM D, YYYY')} &bull; Monitoring warehouse flow and
                pull time compliance
            </p>
        </div>
    );
};

/**
 * Summary alerts showing critical/at-risk counts
 * TODO: Replace with full SummaryAlerts component in Ticket 3c
 */
const SummaryAlerts = ({ summary }) => {
    return (
        <div className="flex gap-4 flex-wrap">
            <div className="flex items-center gap-2 px-4 py-2 bg-red-50 border border-red-200 rounded-lg">
                <span className="text-2xl font-bold text-red-600">{summary.critical}</span>
                <span className="text-red-700">Critical Issues</span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 bg-yellow-50 border border-yellow-200 rounded-lg">
                <span className="text-2xl font-bold text-yellow-600">{summary.atRisk}</span>
                <span className="text-yellow-700">At Risk</span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 bg-green-50 border border-green-200 rounded-lg">
                <span className="text-2xl font-bold text-green-600">{summary.onTrack}</span>
                <span className="text-green-700">On Track</span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 bg-slate-50 border border-slate-200 rounded-lg">
                <span className="text-sm text-slate-600">
                    {summary.totalRoutes} routes &bull; {summary.totalShipments} shipments &bull;{' '}
                    {summary.facilityThroughput}/hr throughput
                </span>
            </div>
        </div>
    );
};

/**
 * Preview route card - basic version for Ticket 3b
 * TODO: Replace with full RouteCard component in Ticket 3d
 */
const RouteCardPreview = ({ route }) => {
    const riskVariant = RISK_BADGE_VARIANTS[route.riskLevel] || 'secondary';
    const riskLabel = RISK_LABELS[route.riskLevel] || route.riskLevel;

    const progressPercent = route.totalShipments > 0 ? (route.transitCount / route.totalShipments) * 100 : 0;

    return (
        <Card>
            <CardHeader className="pb-2">
                <div className="flex justify-between items-start">
                    <div>
                        <Badge variant={riskVariant}>{riskLabel}</Badge>
                    </div>
                </div>
                <CardTitle className="text-lg">{route.hub}</CardTitle>
                <p className="text-sm text-muted-foreground">{route.carrier}</p>
            </CardHeader>
            <CardContent className="space-y-3">
                <div className="text-sm">
                    <span className="font-medium">Pull Time:</span> {route.pullTime?.slice(0, 5)}
                </div>
                <div className="text-sm">
                    <span className="font-medium">Route:</span> {route.pullTime}_
                    {route.carrier === 'dbi' ? 'dbi' : 'carrier'}_{route.marketCode}
                </div>

                {/* Progress summary */}
                <div className="space-y-1">
                    <div className="flex justify-between text-xs text-muted-foreground">
                        <span>Progress</span>
                        <span>
                            {route.transitCount} / {route.totalShipments}
                        </span>
                    </div>
                    <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                        <div className="h-full bg-green-500 transition-all" style={{ width: `${progressPercent}%` }} />
                    </div>
                </div>

                {/* Milestone counts */}
                <div className="grid grid-cols-3 gap-2 text-xs">
                    <div className="text-center">
                        <div className="font-medium">{route.labelsCount}</div>
                        <div className="text-muted-foreground">Labels</div>
                    </div>
                    <div className="text-center">
                        <div className="font-medium">{route.packedCount}</div>
                        <div className="text-muted-foreground">Packed</div>
                    </div>
                    <div className="text-center">
                        <div className="font-medium">{route.transitCount}</div>
                        <div className="text-muted-foreground">Transit</div>
                    </div>
                </div>

                {/* Aging info */}
                {(route.labelsAgingMinutes || route.packingAgingMinutes) && (
                    <div className="text-xs text-muted-foreground">
                        {route.packingAgingMinutes && (
                            <span>Packing: {formatAgingTime(route.packingAgingMinutes)}</span>
                        )}
                        {route.labelsAgingMinutes && route.packingAgingMinutes && ' • '}
                        {route.labelsAgingMinutes && <span>Labels: {formatAgingTime(route.labelsAgingMinutes)}</span>}
                    </div>
                )}
            </CardContent>
        </Card>
    );
};

/**
 * Format aging time in minutes to human readable format
 */
const formatAgingTime = (minutes) => {
    if (minutes === null || minutes === undefined) return '';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
};

/**
 * Skeleton for dashboard header during loading
 */
const DashboardHeaderSkeleton = () => {
    return (
        <div className="space-y-2">
            <Skeleton className="h-8 w-64" />
            <Skeleton className="h-4 w-96" />
        </div>
    );
};

/**
 * Skeleton for route card during loading
 */
const RouteCardSkeleton = () => {
    return (
        <Card>
            <CardHeader className="pb-2">
                <Skeleton className="h-5 w-16" />
                <Skeleton className="h-6 w-12 mt-2" />
                <Skeleton className="h-4 w-20" />
            </CardHeader>
            <CardContent className="space-y-3">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-4 w-48" />
                <Skeleton className="h-2 w-full" />
                <div className="grid grid-cols-3 gap-2">
                    <Skeleton className="h-8" />
                    <Skeleton className="h-8" />
                    <Skeleton className="h-8" />
                </div>
            </CardContent>
        </Card>
    );
};

export default FulfillmentLifecycleDashboard;
